import React, { memo, useCallback, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { MenuVerticalIcon } from '~/assets/icons';
import styles from './JobTypeList.module.scss';
import classNames from 'classnames/bind';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';

import { useTranslation } from 'react-i18next';
import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';
import { Checkbox } from '@mui/material';
import { IJobType } from '~/utils/interface/jobType';
import { jobTypeActions } from '~/thunks/job-type/jobTypeSlice';
import MenuOption from '~/components/equipments/menu-option/MenuOption';

const cx = classNames.bind(styles);

type JobTypeListProps = {
  data?: IJobType;
  onOpenEdit: (id: string) => void;
};

function JobTypeList(props: JobTypeListProps) {
  const permissions = usePermission();
  const { t } = useTranslation(['admin/job-type']);
  const dispatch = useAppDispatch();
  const { jobTypesList } = useAppSelector((state) => state.jobType);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuSelectedId, setMenuSelectedId] = useState<string | undefined>(undefined);
  const [selected, setSelected] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  const isSelected = (id: string) => selected.indexOf(id) !== -1;
  const openOptionMenu = (event: React.MouseEvent<HTMLButtonElement>, itemId: string) => {
    dispatch(jobTypeActions.changeJobTypeId(itemId));
    setAnchorEl(event.currentTarget);
    setMenuSelectedId(itemId);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setMenuSelectedId(undefined);
  };

  const handleEditItem = async () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      props.onOpenEdit(menuSelectedId);
    }
  };

  const handleClick = (event: React.ChangeEvent<unknown>, id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelectAll(jobTypesList.length === newSelected.length);
    setSelected(newSelected);
    setSelectedIds(newSelected);
  };

  const handleSelectAll = () => {
    setSelectAll(!selectAll);
    if (!selectAll) {
      setSelected(jobTypesList.map((item) => item.id));
      setSelectedIds(jobTypesList.map((item) => item.id));
    } else {
      setSelected([]);
      setSelectedIds([]);
    }
  };

  const setSelectedIds = useCallback(
    (selected: Array<string>) => {
      dispatch(jobTypeActions.changeSelectedIds(selected));
    },
    [dispatch]
  );

  const handleDeleteItem = () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      dispatch(jobTypeActions.changeMenuSelectId(menuSelectedId));
      dispatch(jobTypeActions.changeOpenAlertDelete(true));
    }
  };

  return (
    <Box className={cx('job-type-list-container')}>
      <Box className={'flex justify-between items-center gap-[20px] my-3 w-full bg-[#ffffff]'}>
        <Box className='gap-[30px] w-full flex items-center'>
          <Box className='flex-[0_0_2%]'>
            <Checkbox className='w-[20px] h-[20px]' color='primary' checked={selectAll} onChange={handleSelectAll} />
          </Box>
          <Box className='flex-[0_0_45%] text-[16px]/[24px] font-[500] text-[#0d062d]'>{t('properties.name')}</Box>
          <Box className='flex-[0_0_45%] text-[16px]/[24px] font-[500] text-[#0d062d]'>{t('properties.code')}</Box>
          <Box className='flex-[0_0_3%]'>
            <IconButton onClick={(event) => {}}>
              <MenuVerticalIcon className='w-[19px] h-[19px] invisible' />
            </IconButton>
          </Box>
        </Box>
      </Box>
      {jobTypesList?.map((data, index) => (
        <Box
          className='flex justify-between items-center gap-[20px] mt-[20px] w-full bg-[#ffffff] border-b border-[#e0e0e0] pb-[12px]'
          key={index}
        >
          <Box className='gap-[30px] w-full flex items-center '>
            <Box className='flex-[0_0_2%] mt-[20px]'>
              <Checkbox
                className='w-[20px] h-[20px]'
                color='primary'
                onChange={(event) => {
                  handleClick(event, data?.id);
                }}
                checked={isSelected(data?.id)}
              />
            </Box>
            <Box className='flex-[0_0_45%] font-[500]'>
              <Box className='inline-block rounded-[6px] px-[10px] py-[4px] text-[14px] leading-[20px]  mt-[20px]'>
                {data?.name}
              </Box>
            </Box>

            <Box className='flex-[0_0_45%] font-[500]'>{data?.code}</Box>
            {(permissions[PERMISSIONS.jobType_delete] ||
              permissions[PERMISSIONS.jobType_edit] ||
              permissions[PERMISSIONS.jobType_view]) && (
              <Box className='flex-[0_0_3%]'>
                <IconButton
                  onClick={(event) => {
                    openOptionMenu(event, data?.id);
                  }}
                >
                  <MenuVerticalIcon className='w-[19px] h-[19px]' />
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>
      ))}

      <MenuOption
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
      />
    </Box>
  );
}

export default memo(JobTypeList);
